import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { Button, H4, Input, Logo, Pill } from '@base'
import { strings } from '@constants'
import { uploadDocumentWithType } from '@data'
import { submitReceivingAcknowledgement } from '@data/conn/post/extra'
import { CategoryHandler } from '@pages/Login'
import CarouselComponent from '@pages/Login/Carousel'
import { BackgroundImage, CarouselContainer, Container, LoginButton, LoginContainer, LogoContainer, MainWrapper } from '@pages/Login/styles'
import { Spacings } from '@styles'
import { SMALL } from 'src/styles/text'

const InputWrapper = styled.div`
	margin: 0 ${Spacings.SPACING_2B};
`

const HeaderWrapper = styled(H4)`
	margin: ${Spacings.SPACING_3B} ${Spacings.SPACING_3B};
	${({ smallText }) => {
		return (
			smallText &&
			css`
				font-size: ${SMALL};
			`
		)
	}}
`

const SubmitWrapper = styled.div`
	margin: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
`

const ButtonsWrapper = styled.div`
	display: fkex;
	flex-direction: row;
	justify-content: end;
	gap: ${Spacings.SPACING_2B};
`

const PodUploadWrapper = styled.div`
	display: flex;
	justify-content: end;
	margin: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
`

const SubmitMessage = styled.div`
	display: flex;
	margin: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
	color: ${({ theme }) => theme.palette.text.label};
	text-align: center;
	justify-content: center;
`

const deriveFormState = (data) => {
	const _state = {
		rejected_weight: {
			id: 'rejected-weight',
			value: '',
			label: strings('shortage_weight_in_kgs'),
			placeholder: strings(['enter', 'shortage_weight_in_kgs']),
			required: false,
		},
		total_serviced_weight: {
			id: 'total-serviced-weight',
			label: strings(['total_serviced_weight_in_kgs']),
			placeholder: strings(['enter', 'total_serviced_weight']),
			value: '',
			required: false,
		},
		pod_doc: {
			id: 'pod-doc',
			value: '',
		},
	}
	return _state
}

const PodForm = () => {
	const [searchParams] = useSearchParams()
	const [invoiceNumber, seInvoiceoNumber] = useState('')
	const [formState, setFormState] = useState(deriveFormState())
	const fileInputRef = useRef()
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [isError, setIsError] = useState(false)

	useEffect(() => {
		seInvoiceoNumber(searchParams?.get('invoice_number'))
	}, [searchParams])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const showSubmitMessage = () => {
		setIsSubmitted(true)
		setTimeout(() => {
			setIsSubmitted(false)
		}, 5000)
	}

	const showErrorMessage = () => {
		setIsError(true)
		setTimeout(() => {
			setIsError(false)
		}, 5000)
	}

	const handleFileChange = async (e) => {
		const file = e.target.files[0]

		if (!file) {
			return
		}

		uploadDocumentWithType(file, 'DeliveryReceipt')
			.then((response) => {
				updateState('pod_doc')(response?.file_path)
			})
			.catch((error) => {})
			.finally(() => {})
	}

	const handleClick = () => {
		if (fileInputRef.current && !fileInputRef.disabled) {
			fileInputRef.current.click()
		}
	}

	const submitHandler = () => {
		const mobile = searchParams?.get('number')
		submitReceivingAcknowledgement({
			delivery_receipt_file_path: formState?.pod_doc?.value,
			invoice_number: invoiceNumber,
			mobile_number: mobile,
			rejected_weight: formState?.rejected_weight?.value,
			source: 'WEB',
			total_serviced_weight: formState?.total_serviced_weight?.value,
		})
			.then((response) => {})
			.catch((error) => {
				showErrorMessage()
			})
			.finally(() => {
				setFormState(deriveFormState())
				showSubmitMessage()
			})
	}

	const checkDisabled = useMemo(() => {
		if (!formState?.pod_doc?.value || !formState?.total_serviced_weight?.value) {
			return true
		}
		return false
	}, [formState])

	return (
		<MainWrapper>
			<BackgroundImage>
				<Container>
					<CarouselContainer>
						<CarouselComponent />
						{CategoryHandler()}
					</CarouselContainer>
					<LoginContainer>
						<div>
							<LogoContainer>
								<Logo size='4.5rem' isLogin />
							</LogoContainer>
							<HeaderWrapper>{`Please fill necessary POD/Receiving details in the given form :`}</HeaderWrapper>
							<InputWrapper>
								<Pill
									label={strings(['invoice', 'number', ':'])}
									value={invoiceNumber}
									margin={`0 ${Spacings.SPACING_3B} ${Spacings.SPACING_2B} 0 `}
								/>
							</InputWrapper>
							<InputWrapper>
								<Input
									id={formState?.total_serviced_weight?.id}
									type='number'
									label={formState?.total_serviced_weight?.label}
									placeholder={formState?.total_serviced_weight?.placeholder}
									value={formState?.total_serviced_weight?.value}
									required={formState?.total_serviced_weight?.required}
									onChange={updateState('total_serviced_weight')}
								/>
								<Input
									id={formState?.rejected_weight?.id}
									type='number'
									label={formState?.rejected_weight?.label}
									placeholder={formState?.rejected_weight?.placeholder}
									value={formState?.rejected_weight?.value}
									required={formState?.rejected_weight?.required}
									onChange={updateState('rejected_weight')}
								/>
							</InputWrapper>
							<HeaderWrapper smallText>{`Invoice signed/Stamped ; LR ; Weighment slip for Proof of Delivery :`}</HeaderWrapper>
							<PodUploadWrapper>
								<ButtonsWrapper>
									<div>
										<input
											type='file'
											ref={fileInputRef}
											onChange={handleFileChange}
											accept='image/*,.pdf'
											hidden
										/>
										<Button small variant='destructive' type='secondary' onClick={handleClick}>
											{formState?.pod_doc?.value ? strings('change') : strings('upload', 'pod')}
										</Button>
									</div>
								</ButtonsWrapper>
							</PodUploadWrapper>
							<SubmitWrapper>
								<LoginButton disabled={checkDisabled} variant='destructive' onClick={submitHandler}>
									{strings('submit')}
								</LoginButton>
							</SubmitWrapper>
							{isSubmitted && <SubmitMessage>{`Details submitted successfully!`}</SubmitMessage>}
							{isError && <SubmitMessage>{`Some error occurred, Please try again later!`}</SubmitMessage>}
						</div>
					</LoginContainer>
				</Container>
			</BackgroundImage>
		</MainWrapper>
	)
}

export default PodForm
